import { useSearch } from "@tanstack/react-router";
import { useMailbox } from "../../hooks/use-mailbox";
import { useFolders } from "../../hooks/use-folders";
import { ThreadList } from "../../components/mail/thread-list";
import { AggregateThreadList } from "../../components/mail/aggregate-thread-list";
import { SearchResults } from "../../components/mail/search-results";
import type { MailSearchParams } from "../../router";

/**
 * List-mode content for the main pane's "/" route. Self-sufficient (reads
 * `folder`/`q` off the URL itself, mirroring how ThreadDetailRoute reads
 * `threadId`) rather than taking them as props from MailShell — the shell
 * only owns chrome (the folder rail, the persistent search bar, the
 * compose FAB) now that list vs. detail is a real route change instead of
 * a permanently-visible second pane.
 */
export function MailIndexPane() {
  const { mailboxId, selfEmail, isAggregate } = useMailbox();
  const search = useSearch({ strict: false }) as MailSearchParams;
  const { data: foldersData } = useFolders(mailboxId);

  const activeFolder = search.folder ?? "inbox";
  const activeFolderName = foldersData?.folders.find((f) => f.slug === activeFolder)?.name;
  const activeQuery = search.q ?? "";
  const isSearching = !isAggregate && activeQuery.trim().length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!isSearching && (
        <h1 className="px-4 pb-1 pt-3 font-display text-xl text-charcoal">
          {isAggregate ? "All Inboxes" : (activeFolderName ?? "Inbox")}
        </h1>
      )}
      {isAggregate ? (
        <AggregateThreadList folderSlug="inbox" />
      ) : isSearching ? (
        <SearchResults mailboxId={mailboxId} query={activeQuery} />
      ) : (
        <ThreadList mailboxId={mailboxId} folderSlug={activeFolder} folderName={activeFolderName} selfEmail={selfEmail} />
      )}
    </div>
  );
}
